import { Box, Flex, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { useState, useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { Route, useLocation, useSearchParams } from "react-router-dom"
import styled from "styled-components"

import GenericNotFound from "app/generic-not-found"
import Category from "areas/helpdesk/category"
import Collection from "areas/helpdesk/collection"
import Contact from "areas/helpdesk/contact"
import Homepage from "areas/helpdesk/homepage"
import { Breadcrumbs } from "areas/helpdesk/index"
import SearchResults from "areas/helpdesk/search-results"
import WrappedArticle from "areas/helpdesk/wrapped-article"
import Input from "components/ui/input"
import { jewlr_t } from "helpers/application"
import { Routes } from "helpers/sentry"
import useDebounce from "helpers/use-debounce"
import withHelpdesk from "hoc/helpdesk/with-helpdesk"

const Header = styled(Box)`
  text-align: center;
  word-break: break-all;
`
const SearchBar = styled(Box)`
  ${({ theme }) => `
    background-color: ${theme.helpdesk.input.background};
    border: ${theme.helpdesk.input.border};
    box-sizing: border-box;
    font-size:${theme.helpdesk.input.fontSize || "19px"};
    font-weight:500;
    height: ${theme.space[6]};
    letter-spacing: 0.86px;
    line-height: 23px;
    margin-bottom: ${theme.space[4]};
    margin: auto;
    padding: ${theme.helpdesk.input.container_padding};
  `}
`

const metaDescription = `Find help with order information, general questions, ${jewlr_t("terms.jewelry")} education and products, returns, ${jewlr_t("terms.shipping")}, contact info, and phone numbers from Jewlr.`

const Helpdesk = ({
  fetchResults,
  helpdeskArticle,
  helpdeskCollection,
  helpdeskRelatedArticles,
  collections,
  staticContext,
}) => {
  const [searchValue, setSearchValue] = useState("")
  const debouncedSearchTerm = useDebounce(searchValue, 1000)
  const location = useLocation()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    fetchResults(debouncedSearchTerm)
  }, [debouncedSearchTerm])

  useEffect(() => {
    const queryParam = searchParams.get("query")

    if (queryParam && queryParam != searchValue) {
      setSearchValue(queryParam)
      fetchResults(queryParam)
    }
  }, [location.search])

  useEffect(() => {
    if (!location.pathname.includes("search")) {
      setSearchValue("")
    }
  }, [location.pathname])

  const serverNotFound =
    helpdeskArticle?.not_found || helpdeskCollection?.not_found

  if (serverNotFound) {
    return (
      <>
        <Helmet>
          <title>404 Page Not Found</title>
        </Helmet>
        <GenericNotFound staticContext={staticContext} />
      </>
    )
  }

  const showSearchText = ["/helpdesk", "/helpdesk/search"].includes(
    location.pathname
  )

  return (
    <Box contain px="0">
      <Helmet titleTemplate="Customer Service - %s | Jewlr">
        <title>{jewlr_t("terms.Jewelry")} FAQs</title>
        <meta content={metaDescription} name="description" />
        <meta content={metaDescription} property="og:description" />
      </Helmet>
      <Header px={{ _: 3, tablet: 0 }}>
        <Box pb={2} pt={4}>
          {showSearchText && (
            <Box my={4}>
              <Typography
                as="h1"
                color="black"
                fontSize={{ _: 24, tablet: "44px" }}
                fontWeight="300"
                lineHeight={{ _: "29px", tablet: "53px" }}
                mb={2}
              >
                Jewlr Customer Service
              </Typography>
              <p className="body tablet:w-4/5 mx-auto break-normal">
                The Jewlr Helpdesk is your FAQ hub for general questions, order
                information, {jewlr_t("terms.jewelry")} education, and
                international orders, with dedicated sections for returns,
                {jewlr_t("terms.shipping")}, repairs and resizing, and VIP
                Rewards.
              </p>
            </Box>
          )}
          <SearchBar>
            <Flex flexWrap="wrap" px={2}>
              <Box width={1}>
                <Input
                  className="border-none px-0 pb-8 pt-14"
                  id="helpdesk-search"
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search"
                  value={searchValue}
                />
              </Box>
            </Flex>
          </SearchBar>
        </Box>
      </Header>
      <Box contain pb={{ _: 4, tablet: 6 }} px={{ _: 0, tablet: 0 }}>
        <Routes>
          <Route
            element={
              <Breadcrumbs>
                <SearchResults searchValue={searchValue} />
              </Breadcrumbs>
            }
            path="/search"
          />

          <Route
            element={
              <Breadcrumbs>
                <Contact />
              </Breadcrumbs>
            }
            path="/contact"
          />

          <Route
            element={
              <Collection
                helpdeskCollection={helpdeskCollection}
                staticContext={staticContext}
              />
            }
            path="/:collection_id"
          />
          <Route
            element={
              <Category
                helpdeskCollection={helpdeskCollection}
                staticContext={staticContext}
              />
            }
            path="/:collection_id/:category_id"
          />
          <Route
            element={
              <WrappedArticle
                collections={collections}
                helpdeskArticle={helpdeskArticle}
                helpdeskCollection={helpdeskCollection}
                helpdeskRelatedArticles={helpdeskRelatedArticles}
                staticContext={staticContext}
              />
            }
            path="/a/:article_id/:seo?"
          />
          <Route
            element={<Homepage helpdeskCollection={helpdeskCollection} />}
            index
          />
        </Routes>
      </Box>
    </Box>
  )
}
Helpdesk.propTypes = {
  collections: PropTypes.object,
  fetchResults: PropTypes.func,
  helpdeskArticle: PropTypes.object,
  helpdeskCollection: PropTypes.object,
  helpdeskRelatedArticles: PropTypes.array,
  staticContext: PropTypes.object,
}

export default withHelpdesk(Helpdesk)
